import AbstractComponent from './AbstractComponent';
import Logger from '../utils/Logger';
import MediaGallery from './MediaGallery';
import { addClass, removeClass } from '../utils/CssUtils';
import { getPageWidth } from '../utils/JsUtils';
import { addEventListener } from '../utils/EventUtils';
import { convertHTMLStringToDOMElement } from '../utils/HtmlUtils';

export default class PhotosGrid extends AbstractComponent {
    constructor(...args) {
        Logger.log('PhotosGrid->constructor()');
        super(...args);

        this.classname = 'c-photos-grid';
        this.gridColsNumberClassname = this.getClassname('__grid--cols-');
        this.gridContainerClassname = this.getClassname('__grid__container-');
        this.gridItemClassname = this.getClassname('__grid__item');
        this.gridItemLinkClassname = this.getClassname('__grid__item__link');
        this.gridItemLinkSelector = this.getSelector('__grid__item__link');
        this.gridItemImageClassname = this.getClassname('__grid__item__image');
        this.gridItemColorClassname = this.gridItemClassname + '--color-';
        this.currentGridColsNumber = 0;
        this.currentPhotosGallery = null;
        this.initDomElements();
        this.addEventListeners();
    }

    initDomElements() {
        this.$grid = this.$el.querySelector(this.getSelector('__grid'));
        this.$photosData = this.$el.querySelector(this.getSelector('__photos-data'));
    }

    addEventListeners() {
        if (this.$grid && this.$photosData) {
            addEventListener(window, 'resize', this.updatePhotosLayout.bind(this));
            this.initPhotosLayout();
        }

        this.app.addAnchorOpenEventListeners('open-photos-gallery', this.openPhotosGallery.bind(this));
    }

    getPhotosData() {
        if (!this.$photosData) {
            return [];
        }
        const photosDataJson = JSON.parse(this.$photosData.textContent);
        if (!Array.isArray(photosDataJson) || !photosDataJson.length) {
            return [];
        }
        return photosDataJson;
    }

    initPhotosLayout() {
        if (!this.$grid) {
            return;
        }
        const gridColsNumber = this.getGridColsNumber();
        if (this.currentGridColsNumber == gridColsNumber) {
            return;
        }
        this.currentGridColsNumber = gridColsNumber;
        addClass(this.$grid, this.gridColsNumberClassname + this.currentGridColsNumber);
        this.initPhotosLayoutPopulateGrid();
        this.initPhotosGallery();
    }

    initPhotosLayoutPopulateGrid() {
        const photos = this.getPhotosData();

        let possibleHighlightsCount = this.initPhotosLayoutGetPossibleHighlightsCount(photos.length, this.currentGridColsNumber);
        let items = [];
        let highlights = [];
        photos.forEach(item => {
            if ((item.highlight === true) && (highlights.length < possibleHighlightsCount)) {
                highlights.push(item);
            } else {
                items.push(item);
            }
        });

        let isAlternateLayout = false;
        let highlightSize = (2 * 2);
        let blocks = [];
        while ((items.length !== 0) || (highlights.length !== 0)) {
            // Create blocks
            let highlightBlock = { type: 'highlight', items: [] };
            let sideItemsBlock = { type: 'side-items', items: [] };
            let itemsBlock = { type: 'items', items: [] };

            // Check highlights availability
            if (highlights.length === 0) {
                highlightBlock = null;
                sideItemsBlock = null;
            }

            // Determine highlight position
            let highlightPosition = 1;
            if (isAlternateLayout) {
                highlightPosition = (this.currentGridColsNumber - 1);
            }

            // Determine blocks items count
            let blocksItemsCount = (3 * this.currentGridColsNumber);
            if (highlights.length !== 0) {
                blocksItemsCount = (3 * this.currentGridColsNumber) - highlightSize + 1;
            }

            // Iterate on block positions
            for (let index = 1; index <= blocksItemsCount; index++) {
                // Check if a highlight is available
                if (highlightBlock && (index === highlightPosition) && (highlights.length !== 0)) {
                    highlightBlock.items.push(highlights.shift());
                    continue;
                }

                // Check side items
                if ((items.length !== 0)) {
                    if (sideItemsBlock && (index <= (blocksItemsCount - this.currentGridColsNumber))) {
                        sideItemsBlock.items.push(items.shift());
                    } else {
                        itemsBlock.items.push(items.shift());
                    }
                }
            }

            if (isAlternateLayout) {
                // Check side items block
                if (sideItemsBlock && (sideItemsBlock.items.length > 0)) {
                    blocks.push(sideItemsBlock);
                }
                // Check highlight block
                if (highlightBlock && (highlightBlock.items.length > 0)) {
                    blocks.push(highlightBlock);
                }
            } else {
                // Check highlight block
                if (highlightBlock && (highlightBlock.items.length > 0)) {
                    blocks.push(highlightBlock);
                }
                // Check side items block
                if (sideItemsBlock && (sideItemsBlock.items.length > 0)) {
                    blocks.push(sideItemsBlock);
                }
            }

            // Check items block
            if (itemsBlock && (itemsBlock.items.length > 0)) {
                blocks.push(itemsBlock);
            }

            // Alternate layout
            isAlternateLayout = !isAlternateLayout;
        }

        // Iterate on blocks
        blocks.forEach((block) => this.initPhotosLayoutCreateItemsContainerDomElement(this.$grid, block));
    }

    initPhotosLayoutGetPossibleHighlightsCount(count, numCols) {
        let highlights = 0;
        let items = 0;
        let highlightSize = (2 * 2);

        while (items < count) {
            let blocksCount = (3 * numCols) - highlightSize + 1;
            highlights++;
            items += blocksCount;
        }

        return highlights;
    }

    initPhotosLayoutGetSortedPhotos(photos, numCols) {
        let possibleHighlightsCount = this.initPhotosLayoutGetPossibleHighlightsCount(photos.length, numCols);
        let items = [];
        let highlights = [];
        photos.forEach(item => {
            if ((item.highlight === true) && (highlights.length < possibleHighlightsCount)) {
                highlights.push(item);
            } else {
                items.push(item);
            }
        });

        let isAlternateLayout = false;
        let highlightSize = (2 * 2);
        let sortedPhotos = [];
        while ((items.length !== 0) || (highlights.length !== 0)) {
            var highlightPosition = 1;
            if (isAlternateLayout) {
                highlightPosition = (numCols - 1);
            }

            let blocksCount = (3 * numCols);
            if (highlights.length !== 0) {
                blocksCount = (3 * numCols) - highlightSize + 1;
            }

            for (let index = 1; index <= blocksCount; index++) {
                let sortedPhoto = null;
                if ((index === highlightPosition) && (highlights.length !== 0)) {
                    sortedPhoto = highlights.shift();
                }
                else if (items.length !== 0) {
                    sortedPhoto = items.shift();
                }
                if (sortedPhoto) {
                    sortedPhotos.push(sortedPhoto);
                }
            }

            isAlternateLayout = !isAlternateLayout;
        }

        return sortedPhotos;
    }

    initPhotosLayoutCreateItemsContainerDomElement(parent, block) {
        let html = '<div class="';
        html += this.gridContainerClassname;
        html += block.type;
        html += '"></div>';
        const containerElement = convertHTMLStringToDOMElement(html);
        parent.appendChild(containerElement);
        const isHighlight = (block.type === 'highlight');
        block.items.forEach((item) => this.initPhotosLayoutCreateItemDomElement(containerElement, item, isHighlight));
    }

    initPhotosLayoutCreateItemDomElement(parent, item, isHighlight) {
        let html = '<div class="' + this.gridItemClassname + '">';
        html += '    <a class="' + this.gridItemLinkClassname + '"';
        html += '        href="' + item.full_url + '"';
        html += '        target="_blank"';
        html += '        data-pswp-width="' + item.full_width + '"';
        html += '        data-pswp-height="' + item.full_height + '"';
        html += '        data-cropped="true"';
        html += '    >';
        let itemImageSrc = item.thumbnail_small_url;
        if (isHighlight) {
            itemImageSrc = item.thumbnail_large_url;
        }
        html += '        <img class="' + this.gridItemImageClassname + '" src="' + itemImageSrc + '">';
        html += '    </a>';
        html += '</div>';
        const child = convertHTMLStringToDOMElement(html);
        parent.appendChild(child);
    }

    initPhotosLayoutGetModuloItemColorClass(number) {
        return this.gridItemColorClassname + ((number % 8) + 1);
    }

    resetPhotosLayout() {
        if (!this.$grid) {
            return;
        }

        // Reset photos gallery
        this.resetPhotosGallery();

        // Remove all children
        while (this.$grid.firstChild) {
            this.$grid.removeChild(this.$grid.firstChild);
        }

        // Remove old cols number classes
        removeClass(this.$grid, this.gridColsNumberClassname + '3');
        removeClass(this.$grid, this.gridColsNumberClassname + '4');
        removeClass(this.$grid, this.gridColsNumberClassname + '5');
        removeClass(this.$grid, this.gridColsNumberClassname + '6');
        removeClass(this.$grid, this.gridColsNumberClassname + '7');
        removeClass(this.$grid, this.gridColsNumberClassname + '8');
    }

    updatePhotosLayout() {
        const gridColsNumber = this.getGridColsNumber();
        if (this.currentGridColsNumber == gridColsNumber) {
            return;
        }
        this.resetPhotosLayout();
        this.initPhotosLayout();
    }

    getGridColsNumber() {
        const pageWidth = getPageWidth();

        // 3 columns / 0 to 1023 pixels
        if (pageWidth <= 1023) {
            return 3;
        }
        // 4 columns / 1024 to 1407 pixels
        else if (pageWidth <= 1407) {
            return 4;
        }
        // 5 columns / 1408 to 1791 pixels
        else if (pageWidth <= 1791) {
            return 5;
        }
        // 6 columns / 1792 to 2175 pixels
        else if (pageWidth <= 2175) {
            return 6;
        }
        // 7 columns / 2176 to 2559 pixels
        else if (pageWidth <= 2559) {
            return 7;
        }
        // 8 columns / 2560+ pixels
        else if (pageWidth >= 2560) {
            return 8;
        }
    }

    initPhotosGallery() {
        this.currentPhotosGallery = new MediaGallery(this.$grid, this.gridItemLinkSelector);
    }

    resetPhotosGallery() {
        if (this.currentPhotosGallery) {
            this.currentPhotosGallery.destroy();
        }
    }

    openPhotosGallery() {
        if (this.currentPhotosGallery) {
            this.currentPhotosGallery.loadAndOpen(0);
        }
    }

    destroy() { }
}